import React, {useCallback, useEffect, useMemo, useRef} from "react";
import PropTypes from "prop-types";
import {getEvents} from "../../Helpers/EventHelper";
import {sharedEvents, itemNames, pageNames} from "../constants/productSpecification";
import AnalyticsContext from "./AnalyticsContext";

const adaptorPropType = PropTypes.shape({
    init: PropTypes.func,
    setUser: PropTypes.func,
    aliasVisitor: PropTypes.func,
    reset: PropTypes.func,
    trackEvent: PropTypes.func,
});

UniversalAnalyticsProvider.propTypes = {
    adaptors: PropTypes.arrayOf(adaptorPropType),
    productEvents: PropTypes.object,
    user: PropTypes.object,
    children: PropTypes.node.isRequired,
};

UniversalAnalyticsProvider.defaultProps = {
    adaptors: [],
};

/**
 * This class wraps a component and exposes various Analytics related methods
 * to the context api which is then available to all nested components.
 *
 * The implementation for the mixpanel adaptor is in @unibuddy/tracking
 * https://github.com/Unibuddy/tracking/blob/master/src/adaptors/mixpanel.ts
 *
 * The behaviour of Mixpanel alias and set user is defined here:
 * https://github.com/mixpanel/docs/blob/main/legacy/aliases.md#manage-identity-with-alias-and-identify-methods
 *
 * TL;DR:
 * - aliasVisitor (alias in Mixpanel) is used to associate a user with a unique identifier
 * - setUser (identify in Mixpanel) is used to set user properties
 * - aliasVisitor should be called on registration of a user
 * - setUser should be called when user logs back in
 * - unsetUser  (reset in Mixpanel) should be called when user logs out
 */
export default function UniversalAnalyticsProvider({adaptors, user, productEvents, children}) {
    const globalPropertiesRef = useRef({});
    const notFirstRenderRef = useRef(false);

    /**
     * Calls the adaptor setUser method to set the user based on the UUID
     * @param {object} user - user object containing id and account role
     */
    const setUser = useCallback(
        (userObj) => {
            adaptors.forEach((adaptor) => {
                if (!adaptor.setUser) return;

                adaptor.setUser({
                    id: userObj.id,
                    accountRole: userObj.accountRole,
                });
            });
        },
        [adaptors],
    );

    const unsetUser = useCallback(() => {
        adaptors.forEach((adaptor) => {
            if (!adaptor.reset) return;
            adaptor.reset();
        });
    }, [adaptors]);

    useEffect(() => {
        if (user) {
            setUser({
                id: user.id,
                accountRole: user.accountRole,
            });
        } else if (notFirstRenderRef.current) {
            unsetUser();
        }
        notFirstRenderRef.current = true;
    }, [setUser, unsetUser, user]);

    /**
     * Sets a key-value pair that should be sent as a property with every analytics event.
     * @param {string} key - the property key to be sent with every event.
     * @param {string} value- the property value corresponding to the key to be sent with every event.
     */
    const setAnalyticsValue = useCallback((key, value) => {
        globalPropertiesRef.current[key] = value;
    }, []);

    const unsetAnalyticsValue = useCallback((key) => {
        delete globalPropertiesRef.current[key];
    }, []);

    /**
     * Calls the adaptor alias method to alias a visitor to a user id, alias is only called once in the lifecycle of a user
     * @param {object} user - user object containing id and account role
     */
    const aliasVisitor = useCallback(
        (userObj) => {
            adaptors.forEach((adaptor) => {
                if (!adaptor.aliasVisitor) return;

                return adaptor.aliasVisitor({
                    id: userObj.id,
                    accountRole: userObj.accountRole,
                });
            });
        },
        [adaptors],
    );

    /**
     * Calls the adaptor method to record an analytics event with properties.
     * This is the only mandatory adaptor method.
     * @param {string} name - name of the event
     * @param {object} properties - object of key-value pairs to send with the event
     */
    const trackEvent = useCallback(
        (name, properties) => {
            adaptors.forEach((adaptor) => {
                adaptor.trackEvent(name, {...properties, ...globalPropertiesRef.current});
            });
        },
        [adaptors],
    );

    const events = useMemo(
        () => getEvents(trackEvent, sharedEvents, productEvents),
        [productEvents, trackEvent],
    );

    const analytics = useMemo(
        () => ({
            setAnalyticsValue,
            unsetAnalyticsValue,
            trackEvent,
            setUser,
            unsetUser,
            aliasVisitor,
            events,
            itemNames,
            pageNames,
        }),
        [
            aliasVisitor,
            events,
            setAnalyticsValue,
            setUser,
            trackEvent,
            unsetAnalyticsValue,
            unsetUser,
        ],
    );

    return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
}
